/**
 * Tenant Hooks
 *
 * React Query hooks for tenant management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  plansApi,
  tenantApi,
  subscriptionApi,
  billingApi,
  localeApi,
} from '../services/tenant.service';
import { ApiErrorCode, parseApiError } from '@/lib/api-errors';
import { formatTenantDate } from '@/lib/locale-format';
import type { TenantSettings, BillingCycle, TenantPlan } from '../types';

/**
 * Show a mutation error toast with permission-aware wording.
 *
 * On a 403 the generic "Failed to X — Please try again" toast actively
 * misleads — it hides the real reason (no permission) and encourages a
 * retry that cannot succeed. We surface "Access Denied" with a verb-
 * specific description ("update organization settings", "upload
 * organization logo", etc.) so the user knows exactly which capability
 * they lack.
 *
 * For non-403 errors we keep the original generic toast. Infra/5xx and
 * validation errors are independently surfaced by the global
 * mutationCache handler in `query-config.ts` via `apiError.userMessage`.
 */
function showMutationError(
  err: unknown,
  opts: { forbiddenAction: string; genericTitle: string },
): void {
  const apiError = parseApiError(err);
  if (apiError.code === ApiErrorCode.FORBIDDEN) {
    toast.error('Access Denied', {
      description: `You do not have permission to ${opts.forbiddenAction}.`,
    });
    return;
  }
  toast.error(opts.genericTitle, { description: 'Please try again' });
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const tenantKeys = {
  all: ['tenant'] as const,
  current: () => [...tenantKeys.all, 'current'] as const,
  settings: () => [...tenantKeys.all, 'settings'] as const,
  usage: () => [...tenantKeys.all, 'usage'] as const,
  subscription: () => [...tenantKeys.all, 'subscription'] as const,
  plans: () => [...tenantKeys.all, 'plans'] as const,
  publicPlans: () => [...tenantKeys.plans(), 'public'] as const,
  plan: (id: string) => [...tenantKeys.plans(), id] as const,
  billing: () => [...tenantKeys.all, 'billing'] as const,
  invoices: (params?: { page?: number; limit?: number }) =>
    [...tenantKeys.billing(), 'invoices', params] as const,
  invoice: (id: string) => [...tenantKeys.billing(), 'invoice', id] as const,
  upcomingInvoice: () => [...tenantKeys.billing(), 'upcoming'] as const,
  locale: () => [...tenantKeys.all, 'locale'] as const,
  timezones: () => [...tenantKeys.locale(), 'timezones'] as const,
  languages: () => [...tenantKeys.locale(), 'languages'] as const,
};

// =============================================================================
// PLAN QUERIES
// =============================================================================

export function usePlans() {
  return useQuery({
    queryKey: tenantKeys.plans(),
    queryFn: plansApi.getAll,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function usePublicPlans() {
  return useQuery({
    queryKey: tenantKeys.publicPlans(),
    queryFn: plansApi.getPublic,
    staleTime: 1000 * 60 * 30,
  });
}

export function usePlan(id: string) {
  return useQuery({
    queryKey: tenantKeys.plan(id),
    queryFn: () => plansApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 30,
  });
}

// =============================================================================
// TENANT QUERIES
// =============================================================================

export function useCurrentTenant(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.current(),
    queryFn: tenantApi.getCurrent,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
}

export function useTenantSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.settings(),
    queryFn: tenantApi.getSettings,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
}

export function useTenantUsage() {
  return useQuery({
    queryKey: tenantKeys.usage(),
    queryFn: tenantApi.getUsage,
    staleTime: 1000 * 60 * 2, // 2 minutes - more frequent for usage
    refetchInterval: 1000 * 60 * 5, // Auto-refetch every 5 minutes
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: tenantKeys.subscription(),
    queryFn: subscriptionApi.getCurrent,
    staleTime: 1000 * 60 * 5,
  });
}

// =============================================================================
// TENANT MUTATIONS
// =============================================================================

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<TenantSettings>) => tenantApi.updateSettings(settings),

    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({ queryKey: tenantKeys.settings() });
      const previousSettings = queryClient.getQueryData<TenantSettings>(tenantKeys.settings());

      if (previousSettings) {
        queryClient.setQueryData<TenantSettings>(tenantKeys.settings(), {
          ...previousSettings,
          ...newSettings,
        });
      }

      return { previousSettings };
    },

    onError: (err, _newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(tenantKeys.settings(), context.previousSettings);
      }
      showMutationError(err, {
        forbiddenAction: 'update organization settings',
        genericTitle: 'Failed to update settings',
      });
    },

    onSuccess: () => {
      toast.success('Settings Updated', { description: 'Your settings have been saved' });
    },

    onSettled: () => {
      // Invalidate both settings and current tenant (profile data is in both)
      queryClient.invalidateQueries({ queryKey: tenantKeys.settings() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => tenantApi.uploadLogo(file),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.settings() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success('Logo Uploaded', { description: 'Your logo has been updated' });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'upload organization logo',
        genericTitle: 'Failed to upload logo',
      });
    },
  });
}

export function useRemoveLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => tenantApi.removeLogo(),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.settings() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success('Logo Removed', { description: 'Your logo has been removed' });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'remove organization logo',
        genericTitle: 'Failed to remove logo',
      });
    },
  });
}

// =============================================================================
// SUBSCRIPTION MUTATIONS
// =============================================================================

export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { planCode: TenantPlan; cycle: BillingCycle }) =>
      subscriptionApi.upgrade(data),

    onSuccess: (result) => {
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        queryClient.invalidateQueries({ queryKey: tenantKeys.subscription() });
        queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        toast.success('Plan Upgraded', { description: 'Your subscription has been upgraded' });
      }
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'change the subscription plan',
        genericTitle: 'Failed to upgrade plan',
      });
    },
  });
}

export function useDowngradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { planCode: TenantPlan; reason?: string }) =>
      subscriptionApi.downgrade(data),

    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success('Plan Downgrade Scheduled', {
        description: `Your plan will change on ${formatTenantDate(result.effectiveDate)}`,
      });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'change the subscription plan',
        genericTitle: 'Failed to downgrade plan',
      });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { reason: string; feedback?: string }) => subscriptionApi.cancel(data),

    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success('Subscription Cancelled', {
        description: `Your subscription will end on ${formatTenantDate(result.effectiveDate)}`,
      });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'cancel the subscription',
        genericTitle: 'Failed to cancel subscription',
      });
    },
  });
}

export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionApi.reactivate(),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success('Subscription Reactivated', {
        description: 'Your subscription has been reactivated',
      });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'reactivate the subscription',
        genericTitle: 'Failed to reactivate',
      });
    },
  });
}

export function useChangeBillingCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cycle: BillingCycle) => subscriptionApi.changeCycle(cycle),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.billing() });
      toast.success('Billing Cycle Updated', {
        description: 'Your billing cycle has been changed',
      });
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'change the billing cycle',
        genericTitle: 'Failed to change billing cycle',
      });
    },
  });
}

// =============================================================================
// BILLING QUERIES
// =============================================================================

export function useBillingInfo() {
  return useQuery({
    queryKey: tenantKeys.billing(),
    queryFn: billingApi.getInfo,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInvoices(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: tenantKeys.invoices(params),
    queryFn: () => billingApi.getInvoices(params),
    staleTime: 1000 * 60 * 5,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: tenantKeys.invoice(id),
    queryFn: () => billingApi.getInvoice(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 10,
  });
}

export function useUpcomingInvoice() {
  return useQuery({
    queryKey: tenantKeys.upcomingInvoice(),
    queryFn: billingApi.getUpcomingInvoice,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdatePaymentMethod() {
  return useMutation({
    mutationFn: () => billingApi.updatePaymentMethod(),

    onSuccess: (result) => {
      window.location.href = result.checkoutUrl;
    },

    onError: (err) => {
      showMutationError(err, {
        forbiddenAction: 'update the payment method',
        genericTitle: 'Failed to update payment method',
      });
    },
  });
}

// =============================================================================
// LOCALE QUERIES
// =============================================================================

export function useLocaleSettings() {
  return useQuery({
    queryKey: tenantKeys.locale(),
    queryFn: localeApi.getSettings,
    staleTime: 1000 * 60 * 30, // 30 minutes - locale settings rarely change
  });
}

export function useTimezones(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.timezones(),
    queryFn: localeApi.getTimezones,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - timezones are static
  });
}

export function useLanguages(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.languages(),
    queryFn: localeApi.getLanguages,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - languages are static
  });
}
